import React, { useState, useEffect } from 'react';
import { useAuth, apiClient } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  User, Trophy, Flame, Zap, Calendar, Droplets, Beef, Footprints,
  Moon, Salad, CandyOff, Dumbbell, CookingPot, ClipboardList, Brain, Award, Globe, Save, Cake, RefreshCw,
  Pencil, Mail, Lock, Eye, EyeOff, Users
} from 'lucide-react';

const iconMap = {
  droplets: Droplets,
  beef: Beef,
  footprints: Footprints,
  moon: Moon,
  salad: Salad,
  'candy-off': CandyOff,
  dumbbell: Dumbbell,
  'cooking-pot': CookingPot,
  'clipboard-list': ClipboardList,
  brain: Brain,
  award: Award,
};

const Profile = () => {
  const { user, refreshUser } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timezones, setTimezones] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState(user?.timezone || 'America/Vancouver');
  const [savingTimezone, setSavingTimezone] = useState(false);
  const [birthdate, setBirthdate] = useState(user?.birthdate || '');
  const [savingBirthdate, setSavingBirthdate] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Profile editing states
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState(user?.name || '');
  const [savingName, setSavingName] = useState(false);
  
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState(user?.email || '');
  const [savingEmail, setSavingEmail] = useState(false);
  
  const [editingPassword, setEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  
  // Group Milestones visibility
  const [groupMilestonesVisible, setGroupMilestonesVisible] = useState(true);
  const [loadingVisibility, setLoadingVisibility] = useState(true);
  const [savingVisibility, setSavingVisibility] = useState(false);

  const forceRefreshApp = async () => {
    setRefreshing(true);
    toast.info('Clearing app cache...');
    
    try {
      // Clear all storage
      localStorage.removeItem('app_version');
      localStorage.removeItem('app_version_check');
      sessionStorage.clear();
      
      // Clear service worker caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      // Unregister service workers and re-register
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
      }
      
      toast.success('Cache cleared! Reloading app...');
      
      // Force reload
      setTimeout(() => {
        window.location.href = window.location.origin + '?nocache=' + Date.now();
      }, 1000);
    } catch (err) {
      console.error('Force refresh failed:', err);
      toast.error('Refresh failed. Please try again.');
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    fetchTimezones();
    fetchGroupMilestonesVisibility();
  }, []);

  useEffect(() => {
    if (user?.timezone) {
      setSelectedTimezone(user.timezone);
    }
    if (user?.birthdate !== undefined) {
      setBirthdate(user.birthdate || '');
    }
    if (user?.name) {
      setNewName(user.name);
    }
    if (user?.email) {
      setNewEmail(user.email);
    }
  }, [user?.timezone, user?.birthdate, user?.name, user?.email]);

  // Save name handler
  const handleSaveName = async () => {
    if (!newName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setSavingName(true);
    try {
      await apiClient.put('/profile', { name: newName.trim() });
      await refreshUser();
      setEditingName(false);
      toast.success('Name updated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  // Save email handler
  const handleSaveEmail = async () => {
    if (!newEmail.trim()) {
      toast.error('Email cannot be empty');
      return;
    }
    setSavingEmail(true);
    try {
      await apiClient.put('/profile/email', { new_email: newEmail.trim() });
      await refreshUser();
      setEditingEmail(false);
      toast.success('Email updated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  // Save password handler
  const handleSavePassword = async () => {
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setSavingPassword(true);
    try {
      await apiClient.put('/profile/password', { 
        current_password: currentPassword,
        new_password: newPassword 
      });
      setEditingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      toast.success('Password updated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await apiClient.get('/checkins/history?limit=14');
      setHistory(res.data);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimezones = async () => {
    try {
      const res = await apiClient.get('/timezones');
      setTimezones(res.data);
    } catch (err) {
      // Fallback timezones
      setTimezones([
        { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)' },
        { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
        { value: 'America/New_York', label: 'Eastern Time (New York)' },
        { value: 'UTC', label: 'UTC' },
      ]);
    }
  };

  const handleSaveTimezone = async () => {
    setSavingTimezone(true);
    try {
      await apiClient.put('/profile', { timezone: selectedTimezone });
      await refreshUser();
      toast.success('Time zone updated successfully!');
    } catch (err) {
      toast.error('Failed to update time zone');
    } finally {
      setSavingTimezone(false);
    }
  };

  const handleSaveBirthdate = async () => {
    setSavingBirthdate(true);
    try {
      await apiClient.put('/profile', { birthdate: birthdate || '' });
      await refreshUser();
      toast.success('Birthdate updated successfully!');
    } catch (err) {
      toast.error('Failed to update birthdate');
    } finally {
      setSavingBirthdate(false);
    }
  };

  const fetchGroupMilestonesVisibility = async () => {
    try {
      const res = await apiClient.get('/profile/group-milestones-visibility');
      setGroupMilestonesVisible(res.data.visible);
    } catch (err) {
      console.error('Failed to fetch group milestones visibility:', err);
    } finally {
      setLoadingVisibility(false);
    }
  };

  const handleToggleGroupMilestones = async (visible) => {
    setSavingVisibility(true);
    try {
      await apiClient.put(`/profile/group-milestones-visibility?visible=${visible}`);
      setGroupMilestonesVisible(visible);
      toast.success(visible ? 'Your milestones are now visible to the community!' : 'Your milestones are now hidden from Group Wins');
    } catch (err) {
      toast.error('Failed to update visibility setting');
    } finally {
      setSavingVisibility(false);
    }
  };

  const formatBirthdateDisplay = (dateStr) => {
    if (!dateStr) return 'Not set';
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  };

  // Generate last 14 days for calendar
  const generateCalendarDays = () => {
    const days = [];
    for (let i = 13; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const checkin = history.find(h => h.date === dateStr);
      days.push({
        date: dateStr,
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: date.getDate(),
        points: checkin?.total_points || 0,
        hasCheckin: !!checkin
      });
    }
    return days;
  };

  const calendarDays = generateCalendarDays();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-4xl mx-auto">
        {/* Profile Header */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6 animate-fade-in" data-testid="profile-header">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 rounded-2xl bg-[#EFB342]/20 flex items-center justify-center">
              <User size={40} className="text-[#EFB342]" />
            </div>
            <div className="flex-1">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900">{user?.name}</h1>
              <p className="text-stone-600">{user?.email}</p>
              <p className="text-xs text-stone-500 mt-1">
                Member since {new Date(user?.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>

        {/* Account Settings */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6 animate-fade-in" data-testid="account-settings">
          <div className="flex items-center gap-2 mb-4">
            <User className="text-[#EFB342]" size={20} />
            <h2 className="font-heading text-lg font-bold text-stone-900">ACCOUNT SETTINGS</h2>
          </div>
          
          {/* Name */}
          <div className="mb-4 pb-4 border-b border-stone-200">
            <Label className="text-stone-600 text-sm">Name</Label>
            {editingName ? (
              <div className="flex flex-col sm:flex-row gap-3 mt-2">
                <Input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="bg-stone-50 border-stone-300 text-stone-900 flex-1"
                  placeholder="Enter your name"
                  data-testid="profile-name-input"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleSaveName}
                    disabled={savingName}
                    className="bg-[#EFB342] text-black hover:bg-[#D99F30] font-semibold"
                  >
                    {savingName ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingName(false);
                      setNewName(user?.name || '');
                    }}
                    variant="outline"
                    className="border-stone-300 text-stone-700"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between mt-2">
                <p className="text-stone-900 text-base">{user?.name}</p>
                <Button
                  onClick={() => setEditingName(true)}
                  variant="ghost"
                  size="sm"
                  className="text-[#EFB342] hover:bg-[#EFB342]/10"
                >
                  <Pencil size={16} className="mr-1" /> Edit
                </Button>
              </div>
            )}
          </div>

          {/* Email */}
          <div className="mb-4 pb-4 border-b border-stone-200">
            <Label className="text-stone-600 text-sm">Email</Label>
            {editingEmail ? (
              <div className="flex flex-col sm:flex-row gap-3 mt-2">
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="bg-stone-50 border-stone-300 text-stone-900 flex-1"
                  placeholder="Enter your email"
                  data-testid="profile-email-input"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleSaveEmail}
                    disabled={savingEmail}
                    className="bg-[#EFB342] text-black hover:bg-[#D99F30] font-semibold"
                  >
                    {savingEmail ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingEmail(false);
                      setNewEmail(user?.email || '');
                    }}
                    variant="outline"
                    className="border-stone-300 text-stone-700"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between mt-2">
                <p className="text-stone-900 text-base">{user?.email}</p>
                <Button
                  onClick={() => setEditingEmail(true)}
                  variant="ghost"
                  size="sm"
                  className="text-[#EFB342] hover:bg-[#EFB342]/10"
                >
                  <Mail size={16} className="mr-1" /> Edit
                </Button>
              </div>
            )}
          </div>

          {/* Password */}
          <div>
            <Label className="text-stone-600 text-sm">Password</Label>
            {editingPassword ? (
              <div className="mt-2 space-y-3">
                <div className="relative">
                  <Input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="bg-stone-50 border-stone-300 text-stone-900 pr-10"
                    placeholder="Current password"
                    data-testid="profile-current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-700"
                  >
                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <div className="relative">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-stone-50 border-stone-300 text-stone-900 pr-10"
                    placeholder="New password (min 6 characters)"
                    data-testid="profile-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-700"
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="bg-stone-50 border-stone-300 text-stone-900"
                  placeholder="Confirm new password"
                  data-testid="profile-confirm-password-input"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleSavePassword}
                    disabled={savingPassword}
                    className="bg-[#EFB342] text-black hover:bg-[#D99F30] font-semibold"
                  >
                    {savingPassword ? 'Saving...' : 'Update Password'}
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingPassword(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmNewPassword('');
                    }}
                    variant="outline"
                    className="border-stone-300 text-stone-700"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between mt-2">
                <p className="text-stone-900 text-base">••••••••</p>
                <Button
                  onClick={() => setEditingPassword(true)}
                  variant="ghost"
                  size="sm"
                  className="text-[#EFB342] hover:bg-[#EFB342]/10"
                >
                  <Lock size={16} className="mr-1" /> Change
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Time Zone Settings */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6 animate-fade-in" data-testid="timezone-settings">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="text-[#EFB342]" size={20} />
            <h2 className="font-heading text-lg font-bold text-stone-900">TIME ZONE SETTINGS</h2>
          </div>
          <p className="text-sm text-stone-600 mb-4">
            Your daily check-ins reset at midnight in your selected time zone.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
              <SelectTrigger className="bg-stone-50 border-stone-300 text-stone-900 flex-1">
                <SelectValue placeholder="Select your timezone" />
              </SelectTrigger>
              <SelectContent className="bg-stone-50 border-stone-200 max-h-64">
                {timezones.map((tz) => (
                  <SelectItem 
                    key={tz.value} 
                    value={tz.value}
                    className="text-stone-700 focus:bg-[#EFB342]/20 focus:text-stone-900"
                  >
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={handleSaveTimezone}
              disabled={savingTimezone || selectedTimezone === user?.timezone}
              className="bg-[#EFB342] text-black hover:bg-[#D99F30] font-semibold"
            >
              {savingTimezone ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save size={16} />
                  Save
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Birthdate Settings */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6 animate-fade-in" data-testid="birthdate-settings">
          <div className="flex items-center gap-2 mb-4">
            <Cake className="text-[#EFB342]" size={20} />
            <h2 className="font-heading text-lg font-bold text-stone-900">BIRTHDAY</h2>
          </div>
          <p className="text-sm text-stone-600 mb-4">
            {"We'll send you a special birthday message! 🎂"}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input
                type="date"
                value={birthdate}
                onChange={(e) => setBirthdate(e.target.value)}
                className="bg-stone-50 border-stone-300 text-stone-900"
                data-testid="profile-birthdate-input"
              />
              {user?.birthdate && (
                <p className="text-xs text-stone-500 mt-2">
                  Current: {formatBirthdateDisplay(user.birthdate)}
                </p>
              )}
            </div>
            <Button
              onClick={handleSaveBirthdate}
              disabled={savingBirthdate || birthdate === (user?.birthdate || '')}
              className="bg-[#EFB342] text-black hover:bg-[#D99F30] font-semibold"
            >
              {savingBirthdate ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save size={16} />
                  Save
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Group Milestones Privacy Setting */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6 animate-fade-in" data-testid="group-milestones-settings">
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-3">
              <Users className="text-[#EFB342] mt-0.5" size={20} />
              <div>
                <h2 className="font-heading text-lg font-bold text-stone-900 mb-1">Group Wins Visibility</h2>
                <p className="text-stone-600 text-sm">
                  Show your milestone achievements in the Community &quot;Group Wins&quot; section so others can celebrate with you!
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {savingVisibility && (
                <div className="w-4 h-4 border-2 border-[#EFB342] border-t-transparent rounded-full animate-spin" />
              )}
              <Switch
                checked={groupMilestonesVisible}
                onCheckedChange={handleToggleGroupMilestones}
                disabled={loadingVisibility || savingVisibility}
                data-testid="group-milestones-toggle"
              />
            </div>
          </div>
          <p className="text-xs text-stone-500 mt-3 ml-8">
            {groupMilestonesVisible 
              ? '✓ Your milestones are visible to the community' 
              : 'Your milestones are hidden from Group Wins'}
          </p>
        </div>

        {/* App Refresh - Troubleshooting */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6 animate-fade-in" data-testid="app-refresh">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-lg font-bold text-stone-900 mb-1">App Not Loading Properly?</h2>
              <p className="text-stone-600 text-sm">
                If the app looks outdated or won't load, tap here to refresh.
              </p>
            </div>
            <Button
              onClick={forceRefreshApp}
              disabled={refreshing}
              variant="outline"
              className="border-stone-300 text-stone-700 hover:bg-stone-100"
              data-testid="force-refresh-btn"
            >
              {refreshing ? (
                <span className="flex items-center gap-2">
                  <RefreshCw size={16} className="animate-spin" />
                  Refreshing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <RefreshCw size={16} />
                  Refresh App
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 animate-fade-in stagger-1">
          <div className="bg-white border border-stone-200 rounded-2xl p-4 text-center">
            <Trophy className="mx-auto text-[#EFB342] mb-2" size={28} />
            <p className="text-2xl font-bold text-stone-900">{user?.total_points || 0}</p>
            <p className="text-xs text-stone-500 uppercase">Total Points</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-2xl p-4 text-center">
            <Flame className="mx-auto text-orange-500 mb-2" size={28} />
            <p className="text-2xl font-bold text-stone-900">{user?.current_streak || 0}</p>
            <p className="text-xs text-stone-500 uppercase">Current Streak</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-2xl p-4 text-center">
            <Zap className="mx-auto text-purple-500 mb-2" size={28} />
            <p className="text-2xl font-bold text-stone-900">{user?.longest_streak || 0}</p>
            <p className="text-xs text-stone-500 uppercase">Longest Streak</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-2xl p-4 text-center">
            <Award className="mx-auto text-green-500 mb-2" size={28} />
            <p className="text-2xl font-bold text-stone-900">{user?.badges?.length || 0}</p>
            <p className="text-xs text-stone-500 uppercase">Badges</p>
          </div>
        </div>

        {/* Activity Calendar */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6 animate-fade-in stagger-2" data-testid="activity-calendar">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="text-[#EFB342]" size={20} />
            <h2 className="font-heading text-lg font-bold text-stone-900">ACTIVITY (LAST 14 DAYS)</h2>
          </div>
          
          <div className="grid grid-cols-7 gap-2">
            {calendarDays.map((day, i) => (
              <div 
                key={i}
                className={`aspect-square rounded-lg flex flex-col items-center justify-center text-center p-1 ${
                  day.hasCheckin 
                    ? day.points >= 8 
                      ? 'bg-[#EFB342] text-black' 
                      : day.points >= 5 
                        ? 'bg-[#EFB342]/60 text-black' 
                        : 'bg-[#EFB342]/30 text-stone-900'
                    : 'bg-stone-50 text-stone-400'
                }`}
                title={`${day.date}: ${day.points} points`}
              >
                <span className="text-[10px] opacity-70">{day.dayName}</span>
                <span className="text-sm font-bold">{day.dayNum}</span>
                {day.hasCheckin && (
                  <span className="text-[10px] font-semibold">{day.points}pts</span>
                )}
              </div>
            ))}
          </div>
          
          <div className="flex items-center gap-4 mt-4 text-xs text-stone-500">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-stone-50"></div>
              <span>No check-in</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-[#EFB342]/30"></div>
              <span>1-4 pts</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-[#EFB342]/60"></div>
              <span>5-7 pts</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-[#EFB342]"></div>
              <span>8-10 pts</span>
            </div>
          </div>
        </div>

        {/* Badges */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 animate-fade-in stagger-3" data-testid="badges-section">
          <div className="flex items-center gap-2 mb-4">
            <Award className="text-[#EFB342]" size={20} />
            <h2 className="font-heading text-lg font-bold text-stone-900">BADGES EARNED THIS MONTH</h2>
          </div>
          
          {(() => {
            const now = new Date();
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();
            const thisMonthBadges = (user?.badges || []).filter(badge => {
              const earned = new Date(badge.earned_at);
              return earned.getMonth() === currentMonth && earned.getFullYear() === currentYear;
            });
            
            return thisMonthBadges.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {thisMonthBadges.map((badge, i) => {
                  const BadgeIcon = iconMap[badge.badge_icon] || Trophy;
                  return (
                    <div 
                      key={i}
                      className="bg-[#F0EBE1] border border-stone-300 rounded-xl p-4 flex items-center gap-4"
                    >
                      <div className="w-14 h-14 bg-[#EFB342] hexagon flex items-center justify-center flex-shrink-0">
                        <BadgeIcon size={24} className="text-black" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-stone-900 truncate">{badge.badge_name}</p>
                        <p className="text-xs text-stone-500">
                          {new Date(badge.earned_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-stone-50 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy className="text-stone-400" size={32} />
                </div>
                <p className="text-stone-500">No badges earned this month yet</p>
                <p className="text-stone-400 text-sm mt-1">Complete streaks to unlock badges!</p>
              </div>
            );
          })()}
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
